
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    useMultiStyleConfig,
    Button,
    Link as ChakraLink,
    Divider,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* Components */
import Link from '@salesforce/retail-react-app/app/components/link'

/* Form fields */
import NewsletterSignUpFields from '../../forms/newsletter-signup-fields'

/* Hooks */
import {useForm} from 'react-hook-form'
import {useNewsletter} from '../../../integrations/newsletter/hooks/use-newsletter'

/* Icons */
import {AppleLogo, AppleQR, AndroidLogo, AndroidQR, CheckCircleReverted} from '../../icons'

const COMPONENT_CONSTANTS = {
    appleStoreURL: 'https://www.apple.com/ca/app-store/',
    playStoreURL: 'https://play.google.com/store/games?hl=en',
    newsletterType: 'Footer',
    newsletterSource: 'WebSite'
}

const DownloadTheApp = ({...otherProps}) => {
    const styles = useMultiStyleConfig('Newsletter')

    return (
        <Box {...styles.downloadAppContainer} {...otherProps}>
            <Text {...styles.downloadAppTitle} {...styles.largeTypo}>
                <FormattedMessage
                    defaultMessage="Download the app"
                    id="newsletter_download.title"
                />
            </Text>
            <Text {...styles.downloadAppSubTitle} {...styles.xsTypo}>
                <FormattedMessage
                    defaultMessage="Earn 10 points and never miss a fit"
                    id="newsletter_download.subtitle"
                />
            </Text>

            <Flex {...styles.downloadAppStores}>
                <Flex {...styles.downloadAppBox}>
                    <HideOnMobile>
                        <AppleQR {...styles.logoQR} />
                    </HideOnMobile>
                    <Box>
                        <Text {...styles.downloadAppOn} {...styles.xsTypo}>
                            <FormattedMessage
                                defaultMessage="Download on"
                                id="newsletter_download.on"
                            />
                        </Text>
                        <ChakraLink variant="plain"
                            href={COMPONENT_CONSTANTS.appleStoreURL}
                            isExternal
                            target="_blank"
                            rel="noopener noreferrer"
                            {...styles.downloadAppStore}
                            {...styles.xsTypo}>
                            <FormattedMessage
                                defaultMessage="App Store"
                                id="newsletter_download.app_store"
                            />
                        </ChakraLink>
                    </Box>
                    <HideOnDesktop>
                        <Flex {...styles.logoBox}>
                            <AppleLogo {...styles.appleLogo} />
                        </Flex>
                    </HideOnDesktop>
                </Flex>
                <HideOnDesktop>
                    <Divider orientation='vertical' {...styles.dividerStyling}/>
                </HideOnDesktop>
                <Flex {...styles.downloadAppBox}>
                    <HideOnMobile>
                        <AndroidQR {...styles.logoQR} />
                    </HideOnMobile>
                    <Box>
                        <Text {...styles.downloadAppOn} {...styles.xsTypo}>
                            <FormattedMessage
                                defaultMessage="Download on"
                                id="newsletter_download.on"
                            />
                        </Text>
                        <ChakraLink variant="plain"
                            href={COMPONENT_CONSTANTS.playStoreURL}
                            isExternal
                            target="_blank"
                            rel="noopener noreferrer"
                            {...styles.downloadAppStore}
                            {...styles.xsTypo}>
                            <FormattedMessage
                                defaultMessage="Google Play"
                                id="newsletter_download.google_store"
                            />
                        </ChakraLink>
                    </Box>
                    <HideOnDesktop>
                        <Flex {...styles.logoBox}>
                            <AndroidLogo {...styles.androidLogo} />
                        </Flex>
                    </HideOnDesktop>
                </Flex>
            </Flex>
        </Box>
    )
}

DownloadTheApp.propTypes = {
    otherProps: PropTypes.object
}

const NewsletterConfirmation = () => {
    const styles = useMultiStyleConfig('Newsletter')

    return (
        <Flex {...styles.confirmationMessage}>
            <CheckCircleReverted {...styles.checkMarkIcon}/>
            <Text {...styles.xsTypo}>
                <FormattedMessage
                    defaultMessage="Thanks for subscribing"
                    id="newsletter_confirmation.message"
                />
            </Text>
        </Flex>
    )
}

const NewsletterSignUp = ({...otherProps}) => {
    const styles = useMultiStyleConfig('Newsletter')
    const form = useForm({
        mode: 'onSubmit'
    })
    const { setError, formState: { errors } } = form;
    const [newsletterData, setNewsletterData] = useState({
        email: null,
        type: null,
        source: null
    })
    const [submittedWithSuccess, setSubmittedWithSuccess] = useState(false)
    const {submitNewsletter} = useNewsletter({...newsletterData})

    const onSubmit = async (data) => {
        setNewsletterData({
            email: data?.newsletterEmail,
            type: COMPONENT_CONSTANTS.newsletterType,
            source: COMPONENT_CONSTANTS.newsletterSource
        })

        const response = await submitNewsletter();

        if (response?.success) {
            setError('formError', { type: '', message: '' })
            setSubmittedWithSuccess(true)
        } else {
            setError('formError', { type: 'manual', message: response?.errorMessage })
            setSubmittedWithSuccess(false)
        }
    }

    return (
        <Box {...otherProps}>
            <Text {...styles.title} {...styles.largeTypo}>
                <FormattedMessage
                    defaultMessage="Enter your email to stay in the know"
                    id="footer.subscribe.heading.first_to_know"
                />
            </Text>
            {submittedWithSuccess ? (
                <NewsletterConfirmation />
            ) : (
                <>
                    <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                        <Box {...styles.fieldContainer}>
                            <NewsletterSignUpFields form={form} {...styles.inputStyling} data-testid="email-input"/>
                            <Button
                                variant='link'
                                type="submit"
                                title="Submit"
                                data-testid="newsletter-submit-button"
                                {...styles.submitButton}>
                                <FormattedMessage
                                    defaultMessage="Join"
                                    id="newsletter_cta.label"
                                />
                            </Button>

                            {errors.formError && <Text {...styles.fieldError}>{errors.formError.message}</Text>}
                        </Box>
                    </form>

                    <Text {...styles.disclaimerText} {...styles.xsTypo}>
                        <FormattedMessage
                            id="newsletter_disclaimer.text"
                            defaultMessage="By sharing your email address, you agree to receive communications from Garage by email. <policy>Privacy Policy</policy>"
                            values={{
                                policy: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        textDecoration="underline"
                                        to="/privacy-policy.html"
                                        data-testid="privacy-policy-link"
                                    >
                                        {chunks}
                                    </ChakraLink>
                                )
                            }}
                        />
                    </Text>
                </>
            )}
        </Box>
    )
}

NewsletterSignUp.propTypes = {
    otherProps: PropTypes.object
}

export default NewsletterSignUp
